import { hysteriFetch, wrappers } from 'hysterics';
import { buildConsumerReviewQueryParams } from '../utilities/api-utils';
import { ENDPOINT_FETCH_ERROR, ENDPOINT_FETCH_SUCCESS } from '../constants';

const reviewsDataService = hysteriFetch('reviewsData', {
	[wrappers.timeout.contextKey]: {
		timeout: 2000 
	},
	[wrappers.retry.contextKey]: {
		retries: 1
	}
});

/**
 * Validate vehicle data has required fields
 * @param {object} vehicleData - Vehicle data to validate
 * @returns {object} Validation result with isValid flag and error message
 */
const validateVehicleData = (vehicleData) => {
	if (!vehicleData) {
		return {
			isValid: false,
			error: 'Vehicle data is required'
		};
	}

	const requiredFields = ['make', 'model', 'year'];
	const missingFields = requiredFields.filter(field => !vehicleData[field]);

	if (missingFields.length > 0) {
		return {
			isValid: false,
			error: `Missing required fields: ${missingFields.join(', ')}`
		};
	}

	return { isValid: true };
};

/**
 * Helper function to safely track New Relic PageAction events
 * @param {string} action - Action name for New Relic tracking
 * @param {object} facets - Custom attributes to track
 */
const newrelicAction = (action, facets) => {
	if (typeof window !== 'undefined' && window.newrelic) {
		window.newrelic.addPageAction(action, facets);
	}
};

/**
 * Reviews API service
 */
export const reviewsAPI = {
	/**
	 * Fetch paginated reviews for a vehicle
	 * @param {object} vehicleData - Vehicle information (make, model, year)
	 * @param {number} page - Page number to fetch (default: 1)
	 * @param {number} perPage - Number of reviews per page (default: 15)
	 * @param {number} sort - Sort parameter (default: 1 - Most Recent)
	 * @param {number} filter - Filter parameter (default: 5 - All Ratings)
	 * @returns {Promise<object>} Reviews data with pagination info
	 */
	fetchReviews: async (vehicleData, page = 1, perPage = 15, sort = 1, filter = 5) => {
		const requestUrl = '/api/widget/ws-kbb-reviews/consumer-reviews';
		const startTime = performance.now();
		
		// Validate vehicle data first
		const validation = validateVehicleData(vehicleData);
		if (!validation.isValid) {
			// Throw validation error
			const error = new Error(`KBB Reviews API - Validation Error: ${validation.error}`);
			error.isValidationError = true;
			error.vehicleData = vehicleData;
			throw error;
		}

		try {
            // Build query params with defaults applied
			const searchParams = buildConsumerReviewQueryParams({
                make: vehicleData.make,
                model: vehicleData.model,
                year: vehicleData.year,
                page,
                perPage,
                sort,
                filter
            });

            const params = new URLSearchParams(searchParams);

			const url = `${requestUrl}?${params.toString()}`;
			
			const data = await reviewsDataService(url);
			const requestDuration = performance.now() - startTime;
			
			newrelicAction(ENDPOINT_FETCH_SUCCESS, {
				requestUrl,
				responseStatus: 200,
				requestDuration,
				vehicleMake: vehicleData.make,
				vehicleModel: vehicleData.model,
				vehicleYear: vehicleData.year,
				page,
				perPage,
				sort,
				filter,
				resultsCount: data.results?.length || 0,
				totalReviews: data.totalReviews || 0
			});
			
			return data;
		} catch (error) {
			const requestDuration = performance.now() - startTime;
			
			newrelicAction(ENDPOINT_FETCH_ERROR, {
				error: error.message,
				requestUrl,
				requestDuration,
				vehicleMake: vehicleData.make,
				vehicleModel: vehicleData.model,
				vehicleYear: vehicleData.year,
				page,
				sort,
				filter
			});
			
			// Add fetch error flag and re-throw
			error.isFetchError = true;
			error.requestUrl = requestUrl;
			throw error;
		}
	}
};